import type {
  CheckoutCartFragment,
  CheckoutDetailsFragment,
  CheckoutLineDetailsFragment,
  OrderDetailsFragment,
  OrderLineFragment,
  PriceFragment,
  ProductCardFragment,
  ProductVariantCardFragment,
} from '@boc/framework/saleor-graphql';

import type { TAnalyticsItem, TProductPermalink, TProductCardPermalink, TVariantCardPermalink, MinimalVariantForTracking } from './types';

export const getCurrencySymbol = (locale: string, currency: string) =>
  (0)
    .toLocaleString(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(/\d/g, '')
    .trim();

export const currencyData = (price?: PriceFragment | null) => {
  if (!price) {
    return { currency: 'USD', currency_symbol: '$' };
  }
  return {
    currency: price.currency,
    currency_symbol: getCurrencySymbol('en', price.currency),
  };
};

export const variantToItem = (
  variant: MinimalVariantForTracking,
  url: TProductPermalink,
  defaultCurrency: string
): TAnalyticsItem => {
  const price = variant.pricing?.price?.gross;
  return {
    item_id: variant.sku || variant.id,
    item_name: variant.product.name,
    item_variant: variant.name,
    item_category: variant.product.category?.name ?? undefined,
    item_brand: undefined, // Could be extracted from attributes if needed
    price: price?.amount || 0,
    currency: price?.currency || defaultCurrency,
    item_url: url(variant),
    item_image: variant.product.thumbnail?.url,
  };
};

export const productToItem = (
  product: ProductCardFragment,
  url: TProductCardPermalink,
  defaultCurrency: string,
  index?: number
): TAnalyticsItem => {
  const price = product.pricing?.priceRange?.start?.gross;

  return {
    item_id: product.id,
    item_name: product.name,
    item_category: product.category?.name,
    price: price?.amount || 0,
    currency: price?.currency || defaultCurrency,
    item_url: url(product),
    item_image: product.thumbnail?.url,
    ...(index !== undefined ? { index } : {}),
  };
};

export const variantCardToItem = (
  variant: ProductVariantCardFragment,
  url: TVariantCardPermalink,
  defaultCurrency: string,
  index?: number
): TAnalyticsItem => {
  const price = variant.pricing?.price?.gross;

  return {
    item_id: variant.sku || variant.id,
    item_name: variant.product.name,
    item_variant: variant.name,
    item_category: variant.product.category?.name,
    price: price?.amount || 0,
    currency: price?.currency || defaultCurrency,
    item_url: url(variant),
    item_image: variant.product.thumbnail?.url,
    ...(index !== undefined ? { index } : {}),
  };
};

export const lineToItem = (
  line: CheckoutLineDetailsFragment | OrderLineFragment,
  url: TProductPermalink,
  defaultCurrency: string
): TAnalyticsItem | null => {
  if (!line.variant) return null;

  return {
    ...variantToItem(line.variant, url, defaultCurrency),
    quantity: line.quantity,
  };
};

export const checkoutToItems = (
  checkout: CheckoutDetailsFragment | CheckoutCartFragment,
  itemUrl: TProductPermalink
): TAnalyticsItem[] => {
  const defaultCurrency = checkout.totalPrice.gross.currency;
  return checkout.lines
    .map((line) => lineToItem(line, itemUrl, defaultCurrency))
    .filter((item): item is TAnalyticsItem => item !== null);
};

export const orderToItems = (
  order: OrderDetailsFragment,
  itemUrl: TProductPermalink
): TAnalyticsItem[] => {
  const defaultCurrency = order.total.gross.currency;
  return order.lines
    .map((line) => lineToItem(line, itemUrl, defaultCurrency))
    .filter((item): item is TAnalyticsItem => item !== null);
};

export const checkoutValue = (checkout: CheckoutDetailsFragment | CheckoutCartFragment): number => {
  return checkout.totalPrice.gross.amount;
};

export const checkoutCurrency = (checkout: CheckoutDetailsFragment | CheckoutCartFragment): string => {
  return checkout.totalPrice.gross.currency;
};

export const orderValue = (order: OrderDetailsFragment): number => {
  return order.total.gross.amount;
};

export const orderCurrency = (order: OrderDetailsFragment): string => {
  return order.total.gross.currency;
};

export const orderTax = (order: OrderDetailsFragment): number => {
  return order.total.tax.amount;
};

export const orderShipping = (order: OrderDetailsFragment): number => {
  return order.shippingPrice.gross.amount;
};

// Generate unique event ID for deduplication
export const generateEventId = (prefix: string, id: string): string => {
  return `${prefix}_${id}`;
};

export const purchaseEventId = (order: OrderDetailsFragment): string => {
  return generateEventId('purchase', order.id);
};
